/**
 * Solution Visual Studio client route definitions.
 *
 * Two strictly separated route trees:
 *   - EDITOR_ROUTES: Access-protected surface (Cloudflare Access enforced server-side).
 *     Only editor components are referenced here. No viewer/share components are imported.
 *   - SHARE_ROUTES: PIN-gated viewer surface. Only read-only viewer components are
 *     referenced here. No editor components are imported or reachable.
 *
 * Requirements: SEC-01, SEC-04, VIEW-07, GOV-03
 */

import React from "react";

// ---------------------------------------------------------------------------
// Route ID types — distinct branded unions prevent cross-surface confusion
// ---------------------------------------------------------------------------

/** Route IDs valid only within the editor (Access-protected) surface. */
export type EditorRouteId =
  | "editor.library"
  | "editor.files"
  | "editor.library-diagrams"
  | "editor.library-alias"
  | "editor.diagram"
  | "editor.diagram.canvas"
  | "editor.diagram.versions"
  | "editor.diagram.publish"
  | "editor.presentations"
  | "editor.presentation"
  | "editor.presentation.publish"
  /** Legacy path helper IDs retained only to recover pre-shell template links. */
  | "editor.templates"
  | "editor.template"
  | "editor.not-found";

/** Route IDs valid only within the share (PIN-gated viewer) surface. */
export type ShareRouteId =
  | "share.pin-gate"
  | "share.viewer"
  | "share.presentation.pin-gate"
  | "share.presentation.viewer"
  | "share.not-found";

/** Diagram share routes retain the established `/share/:publicId` namespace. */
export type DiagramShareRouteId = "share.pin-gate" | "share.viewer";

/** Presentation shares use a separate public URL and parameter namespace. */
export type PresentationShareRouteId =
  | "share.presentation.pin-gate"
  | "share.presentation.viewer";

/**
 * Route-specific public-ID contracts prevent diagram and presentation share
 * links from being accidentally constructed with the other resource's path.
 */
export interface ShareRouteParams {
  "share.pin-gate": { publicId: string };
  "share.viewer": { publicId: string };
  "share.presentation.pin-gate": { presentationPublicId: string };
  "share.presentation.viewer": { presentationPublicId: string };
}

// ---------------------------------------------------------------------------
// Route descriptor shape
// ---------------------------------------------------------------------------

/**
 * Minimal route descriptor used by the router to match paths and resolve
 * lazy-loaded components. Components are loaded lazily so that editor bundles
 * are never included in the share surface and vice versa.
 */
export interface RouteDescriptor<TId extends string> {
  /** Stable route identifier — used for programmatic navigation. */
  readonly id: TId;
  /**
   * Path pattern. Supports a single `:param` segment.
   * The router matches patterns in declaration order; more specific patterns
   * must appear before catch-all/wildcard entries.
   */
  readonly path: string;
  /**
   * Lazy component loader. Returns a module with a default export that is a
   * React component. Evaluated only when the route is first matched.
   */
  readonly component: () => Promise<{ default: React.ComponentType<object> }>;
  /** Human-readable label for debugging and accessibility. */
  readonly label: string;
  /** Optional guarded destination for a retained legacy route. */
  readonly redirectTo?: string;
}

// ---------------------------------------------------------------------------
// Editor route tree (Access-protected surface)
//
// INVARIANT: No share/viewer component paths appear in this tree.
// Editor routes assume Cloudflare Access is enforced at the Worker level
// (Cf-Access-Jwt-Assertion validated server-side). The client router does NOT
// perform Access validation — that is a server-side responsibility.
// ---------------------------------------------------------------------------

export const EDITOR_ROUTES: ReadonlyArray<RouteDescriptor<EditorRouteId>> = [
  {
    id: "editor.library",
    path: "/",
    label: "Recent Files Dashboard",
    component: () =>
      import("@/client/dashboard/ResourceLibrary").then((m) => ({
        default: m.AllFilesDashboard,
      })),
  },
  {
    id: "editor.files",
    path: "/files",
    label: "All Files Dashboard",
    component: () =>
      import("@/client/dashboard/ResourceLibrary").then((m) => ({
        default: m.AllFilesDashboard,
      })),
  },
  {
    id: "editor.library-diagrams",
    path: "/diagrams",
    label: "Diagram Dashboard",
    component: () =>
      import("@/client/dashboard/ResourceLibrary").then((m) => ({
        default: m.DiagramLibrary,
      })),
    redirectTo: "/files?type=diagram",
  },
  {
    // Alias: Cloudflare Access / Dashboard may redirect the editor URL to
    // /editor. This route renders a client-side redirect back to "/" so the
    // user always lands on the library. The path normalisation in
    // src/entrypoints/editor.tsx handles the common case before mount; this
    // route catches any in-app navigation that arrives at /editor.
    id: "editor.library-alias",
    path: "/editor",
    label: "Editor Redirect",
    component: () =>
      import("@/client/editor/EditorRedirect").then((m) => ({
        default: m.default,
      })),
  },
  {
    id: "editor.presentation.publish",
    path: "/presentations/:presentationId/publish",
    label: "Presentation Publish",
    component: () =>
      import("@/client/presentation-editor/PresentationEditorRoute").then((m) => ({
        default: m.default,
      })),
  },
  {
    id: "editor.presentation",
    path: "/presentations/:presentationId",
    label: "Presentation",
    component: () =>
      import("@/client/presentation-editor/PresentationEditorRoute").then((m) => ({
        default: m.default,
      })),
  },
  {
    id: "editor.presentations",
    path: "/presentations",
    label: "Presentation Library",
    component: () =>
      import("@/client/dashboard/ResourceLibrary").then((m) => ({
        default: m.PresentationLibrary,
      })),
    redirectTo: "/files?type=presentation",
  },
  {
    id: "editor.diagram.canvas",
    path: "/diagrams/:diagramId/canvas",
    label: "Diagram Canvas Alias",
    component: () =>
      import("@/client/editor/AppShell").then((m) => ({
        default: m.default,
      })),
  },
  {
    id: "editor.diagram.versions",
    path: "/diagrams/:diagramId/versions",
    label: "Version Manager",
    component: () =>
      import("@/client/editor/versions/VersionManager").then((m) => ({
        default: m.default,
      })),
  },
  {
    id: "editor.diagram.publish",
    path: "/diagrams/:diagramId/publish",
    label: "Publish Manager",
    component: () =>
      import("@/client/editor/publish/PublishManager").then((m) => ({
        default: m.default,
      })),
  },
  {
    id: "editor.diagram",
    path: "/diagrams/:diagramId",
    label: "Diagram",
    component: () =>
      import("@/client/editor/AppShell").then((m) => ({
        default: m.default,
      })),
  },
  {
    id: "editor.not-found",
    // Catch-all — must be last in the editor tree.
    path: "*",
    label: "Not Found",
    component: () =>
      import("@/client/editor/NotFound").then((m) => ({
        default: m.default,
      })),
  },
] as const;

// ---------------------------------------------------------------------------
// Share route tree (PIN-gated viewer surface)
//
// INVARIANT: No editor component paths appear in this tree.
// Share routes assume the Worker serves a public (no Access) surface. The
// PIN gate is the first route; the viewer is only reachable after the share
// API sets a signed HttpOnly session cookie. No mutation API calls are made
// from any component in this tree.
// ---------------------------------------------------------------------------

export const SHARE_ROUTES: ReadonlyArray<RouteDescriptor<ShareRouteId>> = [
  {
    id: "share.pin-gate",
    path: "/share/:publicId",
    label: "PIN Gate",
    component: () =>
      import("@/client/viewer/PinGate").then((m) => ({
        default: m.default,
      })),
  },
  {
    id: "share.viewer",
    path: "/share/:publicId/view",
    label: "Diagram Viewer",
    component: () =>
      import("@/client/viewer/ShareApp").then((m) => ({
        default: m.default,
      })),
  },
  {
    // Presentations intentionally do not reuse `/share/:publicId`: the public
    // path, route ID, parameter name, cache keys, and API namespace stay
    // resource-specific while preserving existing diagram-share URLs.
    id: "share.presentation.pin-gate",
    path: "/presentations/:presentationPublicId",
    label: "Presentation PIN Gate",
    component: () =>
      import("@/client/viewer/PresentationPinGate").then((m) => ({
        default: m.default,
      })),
  },
  {
    id: "share.presentation.viewer",
    path: "/presentations/:presentationPublicId/view",
    label: "Presentation Viewer",
    component: () =>
      import("@/client/viewer/PresentationShareApp").then((m) => ({
        default: m.default,
      })),
  },
  {
    id: "share.not-found",
    // Catch-all — must be last in the share tree.
    path: "*",
    label: "Not Found",
    component: () =>
      import("@/client/viewer/ShareNotFound").then((m) => ({
        default: m.default,
      })),
  },
] as const;

// ---------------------------------------------------------------------------
// Path helpers
// ---------------------------------------------------------------------------

/** Build a concrete editor path from a route ID and optional params. */
export function editorPath(
  id: EditorRouteId,
  params?: Record<string, string>
): string {
  // Template management no longer has a top-level editor route. Retaining
  // these IDs lets in-flight callers and saved client state recover safely.
  if (id === "editor.templates" || id === "editor.template") return "/";
  const route = EDITOR_ROUTES.find((r) => r.id === id);
  if (!route) throw new Error(`Unknown editor route: ${id}`);
  return resolvePath(route.path, params);
}

/** Build a concrete share path from a route ID and optional params. */
export function sharePath<TId extends ShareRouteId>(
  id: TId,
  ...[params]: TId extends keyof ShareRouteParams
    ? [params: ShareRouteParams[TId]]
    : [params?: Record<string, string>]
): string {
  const route = SHARE_ROUTES.find((r) => r.id === id);
  if (!route) throw new Error(`Unknown share route: ${id}`);
  return resolvePath(route.path, params);
}

function resolvePath(pattern: string, params?: Record<string, string>): string {
  if (!params) return pattern;
  return pattern.replace(/:([a-zA-Z]+)/g, (_, key) => {
    const value = params[key];
    if (value === undefined) {
      throw new Error(`Missing route param: ${key}`);
    }
    return encodeURIComponent(value);
  });
}
